import { useState, useRef, useMemo, useEffect, type FC, type ChangeEvent } from "react";
import { Eye, Image, Info, Plus, Trash2, Upload } from "lucide-react";
import { useSnackbar } from "notistack";

import { TOAST } from "@/shared/constants/toastMessages";

import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { PhoneNumberInput } from "@/shared/components/ui/phone-number-input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";
import { US_STATES } from "@/shared/constants/constants";
import { useAppContext } from "@/shared/hooks/useAppContext";
import {
  useLawyerSettings,
  useUpdateLawyerSettings,
  useLawyerLicensedStates,
  useSaveLicensedStates,
  uploadLawyerPhoto,
  type LicensedState,
} from "@/shared/hooks/useLawyerSettings";
import { useUpdateUser } from "@/shared/hooks/useUpdateUser";
import { buildFullName } from "@/shared/utils/names";
import type { Database } from "@/shared/types/database.types";

type UserUpdate = Database["public"]["Tables"]["users"]["Update"];
type LawyerUpdate = Database["public"]["Tables"]["lawyers"]["Update"];

const TITLE_OPTIONS = [
  { label: "None", value: "none" },
  { label: "Mr.", value: "Mr." },
  { label: "Ms.", value: "Ms." },
  { label: "Mrs.", value: "Mrs." },
  { label: "Dr.", value: "Dr." },
];

export interface ProfileFormData {
  title: string;
  firstName: string;
  lastName: string;
  roleTitle: string;
  workPhone: string;
  cellPhone: string;
  firmName: string;
  licensedStates: LicensedState[];
}

interface ProfileSectionProps {
  onSaved?: () => void;
  onSaveAndPreview?: () => void;
  photoUrl: string | null;
  firmLogoUrl: string | null;
  onPhotoKeyChange: (key: string | null) => void;
  onFirmLogoKeyChange: (key: string | null) => void;
  onIsDirtyChange?: (isDirty: boolean) => void;
  onFormChange?: (data: ProfileFormData) => void;
  saveRef?: React.RefObject<(() => Promise<boolean>) | null>;
  resetRef?: React.RefObject<(() => void) | null>;
}

export const ProfileSection: FC<ProfileSectionProps> = ({
  onSaved,
  onSaveAndPreview,
  photoUrl,
  firmLogoUrl,
  onPhotoKeyChange,
  onFirmLogoKeyChange,
  onIsDirtyChange,
  onFormChange,
  saveRef,
  resetRef,
}) => {
  const { user, authUser, refetch: refetchUser } = useAppContext();
  const { lawyer, refetch: refetchLawyer } = useLawyerSettings();
  const { updateUserAsync } = useUpdateUser();
  const { updateLawyer, isUpdating } = useUpdateLawyerSettings();
  const { data: licensedStatesData } = useLawyerLicensedStates(lawyer?.id);
  const { saveLicensedStates, isSaving: isSavingStates } = useSaveLicensedStates();
  const { enqueueSnackbar } = useSnackbar();

  const photoInputRef = useRef<HTMLInputElement>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);

  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const initialForm = useMemo(
    () => ({
      title: lawyer?.title || "none",
      firstName: user?.first_name ?? "",
      middleName: user?.middle_name ?? "",
      lastName: user?.last_name ?? "",
      roleTitle: lawyer?.role_title ?? "",
      workPhone: user?.phone_number ?? "",
      cellPhone: lawyer?.cell_phone ?? "",
      personalEmail: lawyer?.personal_email ?? "",
      firmName: lawyer?.firm_name ?? "",
      firmAddress: lawyer?.firm_address ?? "",
      firmApartment: lawyer?.firm_apartment ?? "",
      firmCity: lawyer?.firm_city ?? "",
      firmState: lawyer?.firm_state ?? "",
      firmZip: lawyer?.firm_zip ?? "",
      firmPhone: lawyer?.firm_phone ?? "",
      licensedStates:
        licensedStatesData && licensedStatesData.length > 0
          ? licensedStatesData.map((s) => ({
              id: s.id,
              state: s.state,
              bar_number: s.bar_number,
            }))
          : lawyer?.state_of_practice
            ? [{ state: lawyer.state_of_practice, bar_number: lawyer.bar_number ?? "" }]
            : [],
    }),
    [lawyer, user, licensedStatesData],
  );

  const [form, setForm] = useState(initialForm);
  const prevInitialForm = useRef(initialForm);

  useEffect(() => {
    if (prevInitialForm.current !== initialForm) {
      const wasClean = JSON.stringify(prevInitialForm.current) === JSON.stringify(form);
      prevInitialForm.current = initialForm;
      if (wasClean) {
        setForm(initialForm);
      }
    }
  }, [initialForm, form]);

  const isDirty = useMemo(() => {
    const stripIds = (f: typeof form) => ({
      ...f,
      licensedStates: f.licensedStates.map(({ state, bar_number }) => ({ state, bar_number })),
    });
    return JSON.stringify(stripIds(form)) !== JSON.stringify(stripIds(initialForm));
  }, [form, initialForm]);

  useEffect(() => {
    onIsDirtyChange?.(isDirty);
  }, [isDirty, onIsDirtyChange]);

  useEffect(() => {
    onFormChange?.({
      title: form.title,
      firstName: form.firstName,
      lastName: form.lastName,
      roleTitle: form.roleTitle,
      workPhone: form.workPhone,
      cellPhone: form.cellPhone,
      firmName: form.firmName,
      licensedStates: form.licensedStates,
    });
  }, [form, onFormChange]);

  const setField = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleAddState = () => {
    setField("licensedStates", [...form.licensedStates, { state: "", bar_number: "" }]);
  };

  const handleRemoveState = (index: number) => {
    setField(
      "licensedStates",
      form.licensedStates.filter((_, i) => i !== index),
    );
  };

  const handleLicensedStateChange = (index: number, field: keyof LicensedState, value: string) => {
    const updated = form.licensedStates.map((s, i) => (i === index ? { ...s, [field]: value } : s));
    setField("licensedStates", updated);
  };

  const handlePhotoUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingPhoto(true);
    try {
      const fileKey = await uploadLawyerPhoto(file, "lawyer-photos");
      if (!fileKey) {
        enqueueSnackbar("Could not upload photo. Please try again.", { variant: "error" });
        return;
      }
      await updateLawyer({ profile_photo_key: fileKey });
      onPhotoKeyChange(fileKey);
      refetchLawyer();
    } catch {
      enqueueSnackbar("Could not upload photo. Please try again.", { variant: "error" });
    } finally {
      setUploadingPhoto(false);
      if (photoInputRef.current) photoInputRef.current.value = "";
    }
  };

  const handleLogoUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingLogo(true);
    try {
      const fileKey = await uploadLawyerPhoto(file, "firm-logos");
      if (!fileKey) {
        enqueueSnackbar("Could not upload logo. Please try again.", { variant: "error" });
        return;
      }
      await updateLawyer({ firm_logo_key: fileKey });
      onFirmLogoKeyChange(fileKey);
      refetchLawyer();
    } catch {
      enqueueSnackbar("Could not upload logo. Please try again.", { variant: "error" });
    } finally {
      setUploadingLogo(false);
      if (logoInputRef.current) logoInputRef.current.value = "";
    }
  };

  const handleSave = async () => {
    const userUpdates: UserUpdate = {
      first_name: form.firstName,
      middle_name: form.middleName || null,
      last_name: form.lastName,
      phone_number: form.workPhone,
      full_name: buildFullName(form.firstName, form.middleName, form.lastName),
    };

    const lawyerUpdates: LawyerUpdate = {
      title: form.title === "none" ? null : form.title || null,
      role_title: form.roleTitle || null,
      cell_phone: form.cellPhone || null,
      personal_email: form.personalEmail || null,
      firm_name: form.firmName || null,
      firm_address: form.firmAddress || null,
      firm_apartment: form.firmApartment || null,
      firm_city: form.firmCity || null,
      firm_state: form.firmState || null,
      firm_zip: form.firmZip || null,
      firm_phone: form.firmPhone || null,
    };

    try {
      const savePromises: Promise<unknown>[] = [
        updateUserAsync({ user: userUpdates }),
        updateLawyer(lawyerUpdates),
      ];

      if (lawyer?.id) {
        savePromises.push(saveLicensedStates({ lawyerId: lawyer.id, states: form.licensedStates }));
      }

      await Promise.all(savePromises);
      prevInitialForm.current = initialForm;
      refetchUser();
      refetchLawyer();
      enqueueSnackbar(TOAST.CHANGES_SAVED, { variant: "success" });
      return true;
    } catch {
      refetchUser();
      refetchLawyer();
      enqueueSnackbar("Could not save profile. Please try again.", { variant: "error" });
      return false;
    }
  };

  useEffect(() => {
    if (saveRef) saveRef.current = handleSave;
    if (resetRef)
      resetRef.current = () => {
        prevInitialForm.current = initialForm;
        setForm(initialForm);
      };
  }, [form, saveRef, resetRef, initialForm]);

  const handleSaveAndCallback = async (callback?: () => void) => {
    const success = await handleSave();
    if (success) callback?.();
  };

  const actionButtons = (
    <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-3">
      <Button
        variant="outline"
        size="md"
        onClick={onSaveAndPreview}
        disabled={isUpdating || isSavingStates}
        className="w-full sm:w-auto"
      >
        <Eye className="h-4 w-4" />
        Preview profile
      </Button>
      <Button
        disabled={!isDirty || isUpdating || isSavingStates}
        size="md"
        onClick={() => handleSaveAndCallback(onSaved)}
        className="w-full sm:w-auto"
      >
        Save changes
      </Button>
    </div>
  );

  return (
    <div className="rounded-lg border border-primary bg-white overflow-hidden">
      <div className="flex items-start gap-1 p-6">
        <div className="flex-1 flex flex-col gap-1">
          <h4>Your profile</h4>
          <p className="text-secondary">Your details, as clients will see them</p>
        </div>
        <div className="hidden sm:flex">{actionButtons}</div>
      </div>

      <div className="border-t border-primary" />

      <div className="p-6 flex flex-col gap-6">
        <div className="flex flex-col gap-6">
          <p className="text-base font-semibold">Personal information</p>

          <div className="flex flex-col sm:flex-row gap-6 items-start">
            <div className="flex flex-col gap-4 flex-1">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="title">Title</Label>
                <Select value={form.title || undefined} onValueChange={(v) => setField("title", v)}>
                  <SelectTrigger id="title">
                    <SelectValue placeholder="Select title" />
                  </SelectTrigger>
                  <SelectContent>
                    {TITLE_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="first-name">First name</Label>
                <Input
                  id="first-name"
                  placeholder="First name"
                  value={form.firstName}
                  onChange={(e) => setField("firstName", e.target.value)}
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="middle-name">Middle name</Label>
                <Input
                  id="middle-name"
                  placeholder="Middle name"
                  value={form.middleName}
                  onChange={(e) => setField("middleName", e.target.value)}
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="last-name">Last name</Label>
                <Input
                  id="last-name"
                  placeholder="Last name"
                  value={form.lastName}
                  onChange={(e) => setField("lastName", e.target.value)}
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="role-title">Your role</Label>
                <Input
                  id="role-title"
                  placeholder="Partner, Attorney, Of counsel"
                  value={form.roleTitle}
                  onChange={(e) => setField("roleTitle", e.target.value)}
                />
              </div>
            </div>

            <div className="flex flex-col gap-2 flex-1">
              <div className="flex items-center gap-1">
                <Label>Profile photo</Label>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="h-4 w-4 text-secondary cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>
                        Clients are more likely to accept an invitation with a face on it. JPG or
                        PNG.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <div className="flex items-center gap-3">
                <div className="h-[100px] w-[100px] rounded-lg border border-dashed border-primary bg-subtle flex items-center justify-center overflow-hidden">
                  {photoUrl ? (
                    <img
                      src={photoUrl}
                      alt="Profile photo"
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <Image className="h-6 w-6 text-tertiary" aria-hidden="true" />
                  )}
                </div>
                <input
                  ref={photoInputRef}
                  type="file"
                  accept="image/jpeg,image/png"
                  className="sr-only"
                  onChange={handlePhotoUpload}
                  aria-label="Upload profile photo"
                />
                <Button
                  variant="outline"
                  size="md"
                  onClick={() => photoInputRef.current?.click()}
                  disabled={uploadingPhoto}
                >
                  <Upload className="h-4 w-4" />
                  {uploadingPhoto ? "Uploading..." : "Upload photo"}
                </Button>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 flex flex-col gap-1.5">
              <Label htmlFor="professional-email">Professional email</Label>
              <Input
                id="professional-email"
                value={authUser?.email ?? ""}
                disabled
                aria-label="Professional email (read-only)"
              />
            </div>
            <div className="flex-1 flex flex-col gap-1.5">
              <div className="flex items-center gap-1.5">
                <Label htmlFor="personal-email">
                  Personal email <span className="text-tertiary font-normal">(optional)</span>
                </Label>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="h-4 w-4 text-secondary cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>
                        This email is private and will never appear on your client-facing profile.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <Input
                id="personal-email"
                type="email"
                placeholder="example@email.com"
                value={form.personalEmail}
                onChange={(e) => setField("personalEmail", e.target.value)}
              />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 flex flex-col gap-1.5">
              <Label htmlFor="work-phone">Work phone</Label>
              <PhoneNumberInput
                id="work-phone"
                value={form.workPhone}
                onChange={(v) => setField("workPhone", v)}
              />
            </div>
            <div className="flex-1 flex flex-col gap-1.5">
              <div className="flex items-center gap-1.5">
                <Label htmlFor="cell-phone">
                  Cell phone <span className="text-tertiary font-normal">(optional)</span>
                </Label>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="h-4 w-4 text-secondary cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Used for text notifications only</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <PhoneNumberInput
                id="cell-phone"
                value={form.cellPhone}
                onChange={(v) => setField("cellPhone", v)}
              />
            </div>
          </div>
        </div>

        <div className="border-t border-primary" />

        <div className="flex flex-col gap-6">
          <p className="text-base font-semibold">Licensed states</p>

          {form.licensedStates.map((ls, i) => (
            <div key={i} className="flex flex-col sm:flex-row gap-4 sm:items-end">
              <div className="flex-1 flex flex-col gap-1.5">
                <Label>State</Label>
                <Select
                  value={ls.state || undefined}
                  onValueChange={(v) => handleLicensedStateChange(i, "state", v)}
                >
                  <SelectTrigger aria-label="State">
                    <SelectValue placeholder="Select state" />
                  </SelectTrigger>
                  <SelectContent>
                    {US_STATES.map((s) => (
                      <SelectItem key={s.value} value={s.value}>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1 flex flex-col gap-1.5">
                <Label>Bar number / Registration number</Label>
                <Input
                  placeholder="e.g. 123456"
                  inputMode="numeric"
                  value={ls.bar_number}
                  onChange={(e) =>
                    handleLicensedStateChange(
                      i,
                      "bar_number",
                      e.target.value.replace(/[^0-9]/g, ""),
                    )
                  }
                  aria-label="Bar number"
                />
              </div>
              {form.licensedStates.length > 1 && (
                <button
                  onClick={() => handleRemoveState(i)}
                  className="shrink-0 mb-2.5 text-tertiary hover:text-primary transition-colors"
                  aria-label="Remove state"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
          ))}

          <button
            onClick={handleAddState}
            className="flex items-center gap-2 text-brand text-sm font-medium hover:opacity-80 transition-opacity w-fit"
          >
            <Plus className="h-4 w-4" />
            Add a state
          </button>
        </div>

        <div className="border-t border-primary" />

        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-1">
            <p className="text-base font-semibold">Your firm</p>
            <p className="text-secondary">Firm details, shown alongside your profile</p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 flex flex-col gap-1.5">
              <Label htmlFor="firm-name">Firm name</Label>
              <Input
                id="firm-name"
                placeholder="Firm name"
                value={form.firmName}
                onChange={(e) => setField("firmName", e.target.value)}
              />
            </div>
            <div className="flex-1 flex flex-col gap-1.5">
              <Label htmlFor="firm-phone">Firm phone</Label>
              <PhoneNumberInput
                id="firm-phone"
                value={form.firmPhone}
                onChange={(v) => setField("firmPhone", v)}
              />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 flex flex-col gap-1.5">
              <Label htmlFor="firm-address">Firm address</Label>
              <Input
                id="firm-address"
                placeholder="Main office address"
                value={form.firmAddress}
                onChange={(e) => setField("firmAddress", e.target.value)}
              />
            </div>
            <div className="flex-1 flex flex-col gap-1.5">
              <Label htmlFor="firm-apartment">Apartment, suite</Label>
              <Input
                id="firm-apartment"
                placeholder="Optional"
                value={form.firmApartment}
                onChange={(e) => setField("firmApartment", e.target.value)}
              />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 flex flex-col gap-1.5">
              <Label htmlFor="firm-city">City</Label>
              <Input
                id="firm-city"
                placeholder="City"
                value={form.firmCity}
                onChange={(e) => setField("firmCity", e.target.value)}
              />
            </div>
            <div className="flex gap-4 sm:contents">
              <div className="flex-1 sm:w-[133px] sm:flex-none sm:shrink-0 flex flex-col gap-1.5">
                <Label htmlFor="firm-state">State</Label>
                <Select
                  value={form.firmState || undefined}
                  onValueChange={(v) => setField("firmState", v)}
                >
                  <SelectTrigger id="firm-state">
                    <SelectValue placeholder="Select state" />
                  </SelectTrigger>
                  <SelectContent>
                    {US_STATES.map((s) => (
                      <SelectItem key={s.value} value={s.value}>
                        {s.value}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1 sm:w-[145px] sm:flex-none sm:shrink-0 flex flex-col gap-1.5">
                <Label htmlFor="firm-zip">Zip code</Label>
                <Input
                  id="firm-zip"
                  placeholder="Zip code"
                  value={form.firmZip}
                  onChange={(e) => setField("firmZip", e.target.value)}
                  maxLength={10}
                />
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-1">
              <Label>Firm logo</Label>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="h-4 w-4 text-secondary cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>PNG with a transparent background works best</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="h-[70px] w-full sm:w-[200px] rounded-lg border border-dashed border-primary bg-subtle flex items-center justify-center overflow-hidden">
                {firmLogoUrl ? (
                  <img src={firmLogoUrl} alt="Firm logo" className="h-full w-full object-contain" />
                ) : (
                  <Image className="h-6 w-6 text-tertiary" aria-hidden="true" />
                )}
              </div>
              <input
                ref={logoInputRef}
                type="file"
                accept="image/png"
                className="sr-only"
                onChange={handleLogoUpload}
                aria-label="Upload firm logo"
              />
              <Button
                variant="outline"
                size="md"
                onClick={() => logoInputRef.current?.click()}
                disabled={uploadingLogo}
              >
                <Upload className="h-4 w-4" />
                {uploadingLogo ? "Uploading..." : "Upload logo"}
              </Button>
            </div>
          </div>
        </div>

        <div className="border-t border-primary" />

        {actionButtons}
      </div>
    </div>
  );
};
